"use client";

import { c as _c } from "react/compiler-runtime";
import { Link2, Link2Off } from "lucide-react";
import { useState } from "react";
import { chipClass, inputClass, labelClass } from "@/components/form";
import { ImageTool, type ImagePlan } from "@/components/ImageTool";
import { Segmented } from "@/components/Segmented";
import { kindOf, outputName } from "@/lib/files";
import { OUTPUT_FORMATS, outputFormatFor, type OutputFormat } from "@/lib/image/format";
import type { ImageJob } from "@/lib/image/job";
import { SIZE_PRESETS } from "@/lib/image/presets";
import { checkSize, coverCrop, scaleByPercent, sizeFor, toPixels, type Size, type Unit } from "@/lib/image/size";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";
const tool = getTool("resize-image")!;
type Mode = "pixels" | "percent" | "physical";
type Fit = "fill" | "stretch";
type Settings = {
  mode: Mode;
  /** Pixel or cm/inch values as typed ("" = keep the original). */
  width: string;
  height: string;
  lock: boolean;
  /** With the lock on, the side the user typed last; the other follows. */
  driver: "width" | "height";
  fit: Fit;
  percent: string;
  unit: Unit;
  dpi: string;
  format: OutputFormat | "keep";
  limitOn: boolean;
  limitKB: string;
  preset: string | null;
};
const INITIAL: Settings = {
  mode: "pixels",
  width: "",
  height: "",
  lock: true,
  driver: "width",
  fit: "fill",
  percent: "50",
  unit: "cm",
  dpi: "300",
  format: "keep",
  limitOn: false,
  limitKB: "250",
  preset: null
};
const num = (s: string) => s.trim() === "" ? NaN : Number(s);

/** Turns the settings into a job for one image of the batch. */
function jobFor(file: File, source: Size, s: Settings): ImagePlan {
  const format = outputFormatFor(kindOf(file), s.format);
  let target: Size;
  let crop: ImageJob["crop"];
  let dpi: number | undefined;
  if (s.mode === "percent") {
    const p = num(s.percent);
    if (!(p > 0)) return {
      error: "Enter a percentage above 0."
    };
    target = scaleByPercent(source, p);
  } else {
    const toPx = (v: string) => {
      const n = num(v);
      if (Number.isNaN(n)) return NaN;
      return s.mode === "physical" ? toPixels(n, s.unit, num(s.dpi)) : Math.round(n);
    };
    if (s.mode === "physical") {
      dpi = num(s.dpi);
      if (!(dpi >= 10 && dpi <= 2400)) {
        return {
          error: "Enter a DPI between 10 and 2400."
        };
      }
    } else if (s.preset) {
      dpi = SIZE_PRESETS.find(p => p.id === s.preset)?.dpi;
    }
    const w = toPx(s.width);
    const h = toPx(s.height);
    if (s.lock) {
      const side = s.driver === "width" ? w : h;
      target = Number.isNaN(side) ? source : sizeFor(source, {
        width: w,
        height: h
      }, s.driver);
    } else {
      target = {
        width: Number.isNaN(w) ? source.width : w,
        height: Number.isNaN(h) ? source.height : h
      };
      const sameShape = Math.abs(target.width / target.height - source.width / source.height) < 0.01;
      if (s.fit === "fill" && !sameShape) crop = coverCrop(source, target);
    }
  }
  const problem = checkSize(target);
  if (problem) return {
    error: problem
  };
  let maxBytes: number | undefined;
  if (s.limitOn && OUTPUT_FORMATS[format].lossy) {
    const kb = num(s.limitKB);
    if (!(kb >= 1)) return {
      error: "Enter a size limit of at least 1 KB."
    };
    maxBytes = Math.floor(kb * 1024);
  }
  const ext = OUTPUT_FORMATS[format].ext;
  return {
    job: {
      ...target,
      crop,
      format,
      maxBytes,
      dpi
    },
    name: out => outputName(file.name, `${out.width}x${out.height}`, ext)
  };
}
export function ResizeTool() {
  const $ = _c(7);
  const [s, setS] = useState(INITIAL);
  let t0;
  if ($[0] !== s) {
    t0 = (file, source) => jobFor(file, source, s);
    $[0] = s;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== s) {
    t1 = t2 => {
      const {
        file: file_0,
        source: source_0
      } = t2;
      return <ResizeSettings s={s} setS={setS} file={file_0} source={source_0} />;
    };
    $[2] = s;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = <ImageTool tool={tool} zipName="images-resized.zip" plan={t0} actionLabel={_temp} busyVerb="Resizing" settings={t1} />;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp(n) {
  return n > 1 ? `Resize ${n} images` : "Resize image";
}
function ResizeSettings(t0) {
  const $ = _c(68);
  const {
    s,
    setS,
    file,
    source
  } = t0;
  const format = file ? outputFormatFor(kindOf(file), s.format) : "jpg";
  const canLimit = OUTPUT_FORMATS[format].lossy;
  let t1;
  if ($[0] !== setS) {
    t1 = patch => setS(old => ({
      ...old,
      preset: null,
      ...patch
    }));
    $[0] = setS;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const update = t1;
  let t2;
  if ($[2] !== s || $[3] !== source) {
    t2 = function shown(side) {
      if (!s.lock || s.driver === side || !source) {
        return s[side];
      }
      const typed = num(s[s.driver]);
      if (Number.isNaN(typed)) {
        return "";
      }
      const ratio = side === "height" ? source.height / source.width : source.width / source.height;
      return s.mode === "pixels" ? String(Math.max(1, Math.round(typed * ratio))) : String(Math.round(typed * ratio * 100) / 100);
    };
    $[2] = s;
    $[3] = source;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const shown = t2;
  let t3;
  if ($[5] !== s.lock || $[6] !== update) {
    t3 = function typeSide(side_0, value) {
      update(s.lock ? {
        [side_0]: value,
        driver: side_0
      } : {
        [side_0]: value
      });
    };
    $[5] = s.lock;
    $[6] = update;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const typeSide = t3;
  let t4;
  if ($[8] !== s.lock || $[9] !== shown || $[10] !== update) {
    t4 = function toggleLock() {
      update(s.lock ? {
        lock: false,
        width: shown("width"),
        height: shown("height")
      } : {
        lock: true
      });
    };
    $[8] = s.lock;
    $[9] = shown;
    $[10] = update;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const toggleLock = t4;
  let t5;
  if ($[12] !== setS) {
    t5 = function choosePreset(id) {
      const p = SIZE_PRESETS.find(x => x.id === id);
      setS(old_0 => ({
        ...old_0,
        mode: "pixels",
        width: String(p.width),
        height: String(p.height),
        lock: false,
        fit: "fill",
        preset: id,
        ...(p.maxKB ? {
          limitOn: true,
          limitKB: String(p.maxKB)
        } : {}),
        ...(p.maxKB && old_0.format === "png" ? {
          format: "jpg" as const
        } : {})
      }));
    };
    $[12] = setS;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const choosePreset = t5;
  let t6;
  if ($[14] !== update) {
    t6 = mode => update({
      mode
    });
    $[14] = update;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [{
      value: "pixels",
      label: "Pixels"
    }, {
      value: "percent",
      label: "Percent"
    }, {
      value: "physical",
      label: "cm / inch"
    }];
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== s.mode || $[18] !== t6) {
    t8 = <Segmented label="Resize by" stretch={true} value={s.mode} onChange={t6} options={t7} />;
    $[17] = s.mode;
    $[18] = t6;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== s.dpi || $[21] !== s.fit || $[22] !== s.lock || $[23] !== s.mode || $[24] !== s.percent || $[25] !== s.unit || $[26] !== setS || $[27] !== shown || $[28] !== source || $[29] !== toggleLock || $[30] !== typeSide || $[31] !== update) {
    t9 = s.mode === "percent" ? <label className={labelClass}>Percent of the original size<input className={inputClass} inputMode="decimal" value={s.percent} onChange={e => update({
        percent: e.target.value
      })} /></label> : <>{s.mode === "physical" && <div className="flex gap-2.5"><label className={cn(labelClass, "flex-1")}>Unit<select className={inputClass} value={s.unit} onChange={e_0 => update({
            unit: e_0.target.value as Unit
          })}><option value="cm">cm</option><option value="in">inch</option></select></label><label className={cn(labelClass, "flex-1")}>DPI<input className={inputClass} inputMode="numeric" value={s.dpi} onChange={e_1 => update({
            dpi: e_1.target.value
          })} /></label></div>}<div className="flex items-end gap-2.5"><label className={cn(labelClass, "min-w-0 flex-1")}>Width ({s.mode === "pixels" ? "px" : s.unit})<input className={inputClass} inputMode="decimal" placeholder={s.mode === "pixels" && source ? String(source.width) : ""} value={shown("width")} onChange={e_2 => typeSide("width", e_2.target.value)} /></label><button type="button" onClick={toggleLock} aria-pressed={s.lock} aria-label="Keep aspect ratio" title={s.lock ? "Aspect ratio locked" : "Aspect ratio unlocked"} className={cn("flex size-12 shrink-0 items-center justify-center rounded-lg border-[1.5px]", s.lock ? "border-brand bg-brand-tint text-brand-strong" : "border-line-strong text-muted-foreground")}>{s.lock ? <Link2 className="size-5" aria-hidden="true" /> : <Link2Off className="size-5" aria-hidden="true" />}</button><label className={cn(labelClass, "min-w-0 flex-1")}>Height ({s.mode === "pixels" ? "px" : s.unit})<input className={inputClass} inputMode="decimal" placeholder={s.mode === "pixels" && source ? String(source.height) : ""} value={shown("height")} onChange={e_3 => typeSide("height", e_3.target.value)} /></label></div>{!s.lock && <Segmented label="If the shape changes" stretch={true} value={s.fit} onChange={fit => setS(old_1 => ({
        ...old_1,
        fit
      }))} options={[{
        value: "fill",
        label: "Crop to fit"
      }, {
        value: "stretch",
        label: "Stretch"
      }]} />}</>;
    $[20] = s.dpi;
    $[21] = s.fit;
    $[22] = s.lock;
    $[23] = s.mode;
    $[24] = s.percent;
    $[25] = s.unit;
    $[26] = setS;
    $[27] = shown;
    $[28] = source;
    $[29] = toggleLock;
    $[30] = typeSide;
    $[31] = update;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-sm font-semibold" id="quick-sizes">Quick sizes</span>;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== choosePreset || $[35] !== s.preset) {
    t11 = SIZE_PRESETS.map(p_0 => <button key={p_0.id} type="button" aria-pressed={s.preset === p_0.id} onClick={() => choosePreset(p_0.id)} className={cn(chipClass, s.preset === p_0.id ? "border-brand bg-brand-tint font-semibold text-brand-strong" : "border-line-strong bg-card font-medium hover:bg-canvas")}>{p_0.label}</button>);
    $[34] = choosePreset;
    $[35] = s.preset;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t11) {
    t12 = <div className="flex flex-col gap-2">{t10}<div role="group" aria-labelledby="quick-sizes" className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 lg:flex-wrap lg:overflow-visible">{t11}</div></div>;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== setS) {
    t13 = e_4 => setS(old_2 => ({
      ...old_2,
      format: e_4.target.value as Settings["format"]
    }));
    $[39] = setS;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <option value="keep">Keep the format</option>;
    t15 = <option value="jpg">JPG</option>;
    t16 = <option value="png">PNG</option>;
    t17 = <option value="webp">WebP</option>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t14 = $[41];
    t15 = $[42];
    t16 = $[43];
    t17 = $[44];
  }
  let t18;
  if ($[45] !== s.format || $[46] !== t13) {
    t18 = <label className={labelClass}>Save as<select className={inputClass} value={s.format} onChange={t13}>{t14}{t15}{t16}{t17}</select></label>;
    $[45] = s.format;
    $[46] = t13;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const t19 = s.limitOn && canLimit;
  const t20 = !canLimit;
  let t21;
  if ($[48] !== setS) {
    t21 = e_5 => setS(old_3 => ({
      ...old_3,
      limitOn: e_5.target.checked
    }));
    $[48] = setS;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t19 || $[51] !== t20 || $[52] !== t21) {
    t22 = <label className="flex min-h-11 items-center justify-between gap-3 text-[15px] font-semibold">Keep under a file size<input type="checkbox" className="size-[22px] accent-brand" checked={t19} disabled={t20} onChange={t21} /></label>;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== canLimit || $[55] !== s.limitKB || $[56] !== s.limitOn || $[57] !== setS) {
    t23 = !canLimit ? <p className="text-sm text-ink">PNG files can't be squeezed to a size. Save as JPG or WebP to use this.</p> : s.limitOn && <label className="flex items-center gap-2.5 text-sm text-ink">Under<input className={cn(inputClass, "min-h-11 w-24")} inputMode="numeric" aria-label="Size limit in KB" value={s.limitKB} onChange={e_6 => setS(old_4 => ({
        ...old_4,
        limitKB: e_6.target.value
      }))} />KB</label>;
    $[54] = canLimit;
    $[55] = s.limitKB;
    $[56] = s.limitOn;
    $[57] = setS;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t22 || $[60] !== t23) {
    t24 = <div className="flex flex-col gap-2.5 rounded-lg bg-canvas p-3.5">{t22}{t23}</div>;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t12 || $[63] !== t18 || $[64] !== t24 || $[65] !== t8 || $[66] !== t9) {
    t25 = <>{t8}{t9}{t12}{t18}{t24}</>;
    $[62] = t12;
    $[63] = t18;
    $[64] = t24;
    $[65] = t8;
    $[66] = t9;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  return t25;
}